import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  PageHeader,
  StatusBadge,
  ApproverRoleBadge,
  EmptyState,
} from "./shared/components";
import {
  getPendingApprovals,
  formatDate,
  PendingApprovalItem,
} from "./services/eSeparation.service";

const TYPE_LABELS: Record<string, string> = {
  RESIGNATION: "Resignation",
  SHORT_NOTICE: "Short Notice",
  WITHDRAWAL: "Withdrawal",
  NO_DUES: "No-Dues",
  Intimation: "Resignation",
  ShortNotice: "Short Notice",
  Withdrawal: "Withdrawal",
  NoDues: "No-Dues",
};

const isNoDuesType = (t: string) =>
  String(t || "").toUpperCase().includes("NO_DUES") ||
  String(t || "").toUpperCase().includes("NODUES");

const HRVerification: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;

  const [requests, setRequests] = useState<PendingApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    getPendingApprovals("HR")
      .then((res) => setRequests(res.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((item) => {
      const name = String(item.emp_name || "").toLowerCase();
      const empNo = String(item.emp_no || "");
      const reqId = String(item.request_id || "").toLowerCase();
      return name.includes(q) || empNo.includes(search) || reqId.includes(q);
    });
  }, [requests, search]);

  const openDetail = (item: PendingApprovalItem) => {
    const type = String(item.request_type || item.type || "");
    if (isNoDuesType(type)) {
      navigate(routes.Esepration.noDuesDispatch, {
        state: { request_id: item.request_id },
      });
      return;
    }
    // A withdrawal is approved by plain sequential approvers (HR levels included),
    // so it opens the withdrawal view (withdrawal form + "View Complete Details"
    // for the original resignation) — never the resignation-specific HR screen.
    if (type.toUpperCase().includes("WITHDRAW")) {
      navigate(`${routes.Esepration.approvals}/${item.request_id}`, {
        state: { request_id: item.request_id, type: "Withdrawal", emp_no: item.emp_no, role: "HR" },
      });
      return;
    }
    // A caller who is both an HR-roster member and (separately) a Competent
    // Authority for some other request would otherwise have their CA item
    // routed to the wrong screen here too — same branch as PendingApprovals.
    // Opens the same ApprovalDetail.tsx screen every other approver uses.
    if (item.is_ca_item) {
      navigate(`${routes.Esepration.approvals}/${item.request_id}`, {
        state: { request_id: item.request_id, type: item.type, emp_no: item.emp_no, role: "HR" },
      });
      return;
    }
    navigate(`/e-sepration/hr-verification/${item.request_id}`, {
      state: { request_id: item.request_id, emp_no: item.emp_no, role: "HR" },
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="HR Verification & Processing"
          subtitle="Tap a request to verify and forward to CA"
          right={<ApproverRoleBadge role="HR" />}
          onExit={() => navigate(-1)}
        />

        {/* Search + count */}
        <div
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 12 }}
        >
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <div className="input-icon flex-grow-1" style={{ minWidth: 240 }}>
              <span className="input-icon-addon">
                <i className="ti ti-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, employee no. or request ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="badge bg-warning-transparent text-warning fs-12 px-3 py-2">
              {filtered.length} to verify
            </span>
            <button
              className="btn btn-outline-secondary"
              onClick={load}
              title="Refresh"
            >
              <i className="ti ti-refresh" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
            <p className="text-muted mt-3 mb-0">Loading HR inbox...</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ti-file-stack"
            title="No Requests to Verify"
            message="Resignations that reach HR will appear here. Use refresh to check for new requests."
          />
        ) : (
          <div className="row g-3">
            {filtered.map((item) => {
              const type = String(
                item.request_type || item.type || "RESIGNATION"
              );
              const noDues = isNoDuesType(type);
              const accent = noDues ? "#993C1D" : "#BA7517";
              const iconBg = noDues ? "#FDE8E0" : "#FFF1DE";
              const cardIcon = noDues ? "ti-clipboard-list" : "ti-file-text";
              return (
                <div className="col-md-6 col-xl-4" key={item.request_id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{
                      borderRadius: 14,
                      borderLeft: `4px solid ${accent}`,
                      cursor: "pointer",
                    }}
                    onClick={() => openDetail(item)}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span
                          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 11,
                            background: iconBg,
                            color: accent,
                          }}
                        >
                          <i className={`ti ${cardIcon}`} style={{ fontSize: 18 }} />
                        </span>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate">
                            {item.emp_name || "—"}
                          </div>
                          <small className="text-muted text-truncate d-block">
                            {item.emp_no} • {item.designation || "—"}
                          </small>
                        </div>
                        <StatusBadge status={item.status || "REACHED_HR"} />
                      </div>

                      <div className="d-flex align-items-center justify-content-between border-top pt-2">
                        <span className="text-muted small d-inline-flex align-items-center gap-1">
                          <i className="ti ti-tag" />
                          {item.request_id}
                        </span>
                        <span className="text-muted small d-inline-flex align-items-center gap-1">
                          <i className="ti ti-calendar" />
                          {formatDate(item.submitted_date) || "—"}
                        </span>
                      </div>

                      <div className="mt-2">
                        <span
                          className="badge bg-light text-muted text-uppercase"
                          style={{ fontSize: 10, letterSpacing: 0.5 }}
                        >
                          {TYPE_LABELS[type] || type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HRVerification;
